// src/pages/Trend/components/analysis/market-trends/SeasonTrendChart.js
import React, { memo, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Sun, Cloud, Leaf, Snowflake } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';
import './MarketResponseChart.css';

const COLORS = {
  PRIMARY: BBC_STYLE.PRIMARY_COLOR,
  SECONDARY: BBC_STYLE.SECONDARY_COLOR,
  SPRING: '#4CAF50',   // Fresh green for Spring
  SUMMER: '#FF9800',   // Warm orange for Summer
  FALL: '#F44336',     // Deep red for Fall
  WINTER: '#2196F3'    // Cool blue for Winter
};

// Custom triangle bar component with gradient and inner highlight
const TriangleBar = memo((props) => {
  const { fill, x, y, width, height, payload, selectedSeason } = props;

  if (!payload) return null;

  const season = payload.season;
  const isHighlighted = !selectedSeason || selectedSeason === season;

  // Calculate triangle points (pointing upward)
  const centerX = x + width / 2;
  const topY = y;
  const bottomY = y + height;
  const leftX = x;
  const rightX = x + width;

  const points = `${centerX},${topY} ${rightX},${bottomY} ${leftX},${bottomY}`;

  const uniqueId = `triangle-gradient-${payload.month?.replace(/\s/g, '')}-${Math.random()}`;

  return (
    <g>
      {/* Define gradient for this triangle */}
      <defs>
        <linearGradient id={uniqueId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fill} stopOpacity={isHighlighted ? 0.95 : 0.35} />
          <stop offset="100%" stopColor={fill} stopOpacity={isHighlighted ? 0.75 : 0.2} />
        </linearGradient>
      </defs>

      {/* Main triangle with gradient */}
      <polygon
        points={points}
        fill={`url(#${uniqueId})`}
        stroke={fill}
        strokeWidth={isHighlighted ? 1.5 : 1}
        strokeOpacity={isHighlighted ? 0.9 : 0.4}
        style={{
          transition: 'all 0.2s ease'
        }}
      />

      {/* Inner highlight line for depth effect */}
      {isHighlighted && (
        <line
          x1={centerX}
          y1={topY + 4}
          x2={centerX}
          y2={bottomY - 4}
          stroke="white"
          strokeWidth={1.5}
          strokeOpacity={0.35}
          strokeLinecap="round"
        />
      )}
    </g>
  );
});

const CustomTooltip = ({ active, payload, label, hasSecondaryData }) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
      minWidth: '160px'
    }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: '#999999',
        marginBottom: '6px'
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '10px',
        color: '#999999',
        marginBottom: '10px',
        paddingBottom: '8px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        {dataPoint.season}
      </div>

      {/* Primary value */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '3px 0',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR
          }} />
          <span style={{ fontSize: '11px', color: '#666666' }}>
            {hasSecondaryData ? 'Set 1' : 'Value'}
          </span>
        </div>
        <span style={{
          fontSize: '11px',
          fontWeight: '500',
          fontFamily: BBC_STYLE.FONT_FAMILY,
          color: BBC_STYLE.TEXT_COLOR
        }}>
          {dataPoint.value.toFixed(1)}%
        </span>
      </div>

      {/* Secondary value if exists */}
      {hasSecondaryData && dataPoint.value2 !== undefined && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '3px 0',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: BBC_STYLE.SECONDARY_COLOR
            }} />
            <span style={{ fontSize: '11px', color: '#666666' }}>
              Set 2
            </span>
          </div>
          <span style={{
            fontSize: '11px',
            fontWeight: '500',
            fontFamily: BBC_STYLE.FONT_FAMILY,
            color: BBC_STYLE.TEXT_COLOR
          }}>
            {dataPoint.value2.toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
};

// Create narrative title based on seasonal data insights
const createNarrativeTitle = (processedData, seasonalConfig, hasSecondaryData, translate) => {
  if (!processedData || processedData.length === 0) return null;

  // Calculate average by season for primary data
  const seasonAverages = {};
  const seasonAverages2 = {};

  Object.keys(seasonalConfig).forEach(season => {
    const seasonData = processedData.filter(item => item.season === season);
    if (seasonData.length > 0) {
      seasonAverages[season] = Math.round(
        seasonData.reduce((sum, item) => sum + item.value, 0) / seasonData.length
      );

      if (hasSecondaryData) {
        seasonAverages2[season] = Math.round(
          seasonData.reduce((sum, item) => sum + (item.value2 || 0), 0) / seasonData.length
        );
      }
    }
  });

  // Find highest season for both datasets
  const highestSeason1 = Object.entries(seasonAverages).reduce((a, b) =>
    a[1] > b[1] ? a : b
  );

  const seasonName1 = highestSeason1[0];

  if (!hasSecondaryData) {
    // Single narrative without comparison
    return (
      <>
        <span style={{
          color: seasonalConfig[highestSeason1[0]]?.color,
          fontWeight: '700',
          borderBottom: `2px solid ${seasonalConfig[highestSeason1[0]]?.color}`,
          paddingBottom: '1px'
        }}>
          {seasonName1}
        </span>
        {' '}{translate('trendPage.marketTrends.narratives.season.single.mostActive')
          .replace('{season}', seasonName1)
          .replace('{percentage}', highestSeason1[1])}
      </>
    );
  }

  // Comparison mode
  const highestSeason2 = Object.entries(seasonAverages2).reduce((a, b) =>
    a[1] > b[1] ? a : b
  );
  const seasonName2 = highestSeason2[0];
  const sameSeason = highestSeason1[0] === highestSeason2[0];

  // Combined insight
  const combinedInsight = sameSeason
    ? translate('trendPage.marketTrends.narratives.season.comparison.sameSeason', { season: seasonName1 })
    : translate('trendPage.marketTrends.narratives.season.comparison.differentSeasons');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      {/* Combined insight first */}
      <div style={{
        fontSize: '1.3rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR
      }}>
        {combinedInsight}
      </div>

      {/* Individual narratives */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        fontSize: '0.95rem'
      }}>
        {/* Primary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set1')}:{' '}
            <span style={{
              fontWeight: '700',
              color: seasonalConfig[highestSeason1[0]]?.color,
              backgroundColor: 'rgba(19, 128, 161, 0.08)', // Light blue background for filter 1
              padding: '2px 6px',
              borderRadius: '3px'
            }}>
              {seasonName1}
            </span>
            {' '}({translate('trendPage.marketTrends.narratives.season.comparison.avgResponse')} {highestSeason1[1]}%)
          </span>
        </div>

        {/* Secondary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set2')}:{' '}
            <span style={{
              fontWeight: '700',
              color: seasonalConfig[highestSeason2[0]]?.color,
              backgroundColor: 'rgba(250, 171, 24, 0.08)', // Light orange background for filter 2
              padding: '2px 6px',
              borderRadius: '3px'
            }}>
              {seasonName2}
            </span>
            {' '}({translate('trendPage.marketTrends.narratives.season.comparison.avgResponse')} {highestSeason2[1]}%)
          </span>
        </div>
      </div>
    </div>
  );
};

// Create subtitle with key insights - Single line format with vertical bar separator
const createSubtitle = (processedData, hasSecondaryData, translate) => {
  if (!processedData || processedData.length === 0) return null;

  // Calculate yearly average for primary
  const yearlyAverage1 = Math.round(
    processedData.reduce((sum, item) => sum + item.value, 0) / processedData.length
  );

  if (!hasSecondaryData) {
    // Find max and min months for single dataset
    const maxMonth = processedData.reduce((a, b) => a.value > b.value ? a : b);
    const minMonth = processedData.reduce((a, b) => a.value < b.value ? a : b);

    return (
      <>
        {translate('trendPage.marketTrends.narratives.season.single.yearlyAvg')
          .replace('{percentage}', yearlyAverage1)}
        <span style={{
          margin: '0 10px',
          color: '#D0D0D0',
          fontWeight: '300'
        }}>｜</span>
        <span style={{ color: BBC_STYLE.POSITIVE_COLOR, fontWeight: '600' }}>↑</span> {' '}
        <span style={{
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          backgroundColor: 'rgba(46, 125, 50, 0.08)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          {translate('trendPage.marketTrends.narratives.season.single.highest')
            .replace('{month}', maxMonth.month)
            .replace('{percentage}', Math.round(maxMonth.value))}
        </span>
        <span style={{
          margin: '0 10px',
          color: '#D0D0D0',
          fontWeight: '300'
        }}>｜</span>
        <span style={{ color: BBC_STYLE.NEGATIVE_COLOR, fontWeight: '600' }}>↓</span> {' '}
        <span style={{
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          backgroundColor: 'rgba(213, 94, 0, 0.08)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          {translate('trendPage.marketTrends.narratives.season.single.lowest')
            .replace('{month}', minMonth.month)
            .replace('{percentage}', Math.round(minMonth.value))}
        </span>
      </>
    );
  }

  // Comparison mode
  const yearlyAverage2 = Math.round(
    processedData.reduce((sum, item) => sum + (item.value2 || 0), 0) / processedData.length
  );
  const avgDiff = Math.abs(yearlyAverage1 - yearlyAverage2);

  return (
    <>
      {translate('trendPage.filters.set1')}: {' '}
      <span style={{
        fontWeight: '700',
        fontSize: '1.15rem',
        color: BBC_STYLE.PRIMARY_COLOR,
        backgroundColor: 'rgba(19, 128, 161, 0.08)',
        padding: '2px 6px',
        borderRadius: '3px'
      }}>
        {yearlyAverage1}%
      </span>
      {' '}{translate('trendPage.marketTrends.narratives.season.comparison.avgResponse')}
      <span style={{
        margin: '0 10px',
        color: '#D0D0D0',
        fontWeight: '300'
      }}>｜</span>
      {translate('trendPage.filters.set2')}: {' '}
      <span style={{
        fontWeight: '700',
        fontSize: '1.15rem',
        color: BBC_STYLE.SECONDARY_COLOR,
        backgroundColor: 'rgba(250, 171, 24, 0.08)',
        padding: '2px 6px',
        borderRadius: '3px'
      }}>
        {yearlyAverage2}%
      </span>
      {' '}{translate('trendPage.marketTrends.narratives.season.comparison.avgResponse')}
      {avgDiff > 5 && (
        <>
          <span style={{
            margin: '0 10px',
            color: '#D0D0D0',
            fontWeight: '300'
          }}>｜</span>
          <span style={{
            fontWeight: '600',
            fontSize: '0.9rem',
            color: BBC_STYLE.NEGATIVE_COLOR,
            backgroundColor: 'rgba(213, 94, 0, 0.08)',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>
            {translate('trendPage.marketTrends.narratives.season.comparison.responseDiff', { diff: avgDiff })}
          </span>
        </>
      )}
    </>
  );
};

const SeasonTrendChart = ({ marketResponseData }) => {
  const { translate } = useLanguage();
  const [selectedSeason, setSelectedSeason] = React.useState(null);

  // Create month mapping function to translate Korean month labels
  const translateMonth = useMemo(() => {
    const monthMapping = {
      '1월': translate('trendPage.marketTrends.months.january'),
      '2월': translate('trendPage.marketTrends.months.february'),
      '3월': translate('trendPage.marketTrends.months.march'),
      '4월': translate('trendPage.marketTrends.months.april'),
      '5월': translate('trendPage.marketTrends.months.may'),
      '6월': translate('trendPage.marketTrends.months.june'),
      '7월': translate('trendPage.marketTrends.months.july'),
      '8월': translate('trendPage.marketTrends.months.august'),
      '9월': translate('trendPage.marketTrends.months.september'),
      '10월': translate('trendPage.marketTrends.months.october'),
      '11월': translate('trendPage.marketTrends.months.november'),
      '12월': translate('trendPage.marketTrends.months.december')
    };

    return (koreanMonth) => monthMapping[koreanMonth] || koreanMonth;
  }, [translate]);

  // Create seasonal configuration with translations
  const seasonalConfig = useMemo(() => ({
    [translate('trendPage.marketTrends.seasons.spring')]: {
      icon: Sun,
      color: COLORS.SPRING,
      months: [3, 4, 5] // Month numbers for mapping
    },
    [translate('trendPage.marketTrends.seasons.summer')]: {
      icon: Cloud,
      color: COLORS.SUMMER,
      months: [6, 7, 8]
    },
    [translate('trendPage.marketTrends.seasons.fall')]: {
      icon: Leaf,
      color: COLORS.FALL,
      months: [9, 10, 11]
    },
    [translate('trendPage.marketTrends.seasons.winter')]: {
      icon: Snowflake,
      color: COLORS.WINTER,
      months: [12, 1, 2]
    }
  }), [translate]);

  // Function to get season by Korean month label
  const getSeasonByKoreanMonth = useMemo(() => {
    return (koreanMonth) => {
      // Extract month number from Korean month (e.g., "3월" -> 3)
      const monthNumber = parseInt(koreanMonth.replace('월', ''));

      if ([3, 4, 5].includes(monthNumber)) return translate('trendPage.marketTrends.seasons.spring');
      if ([6, 7, 8].includes(monthNumber)) return translate('trendPage.marketTrends.seasons.summer');
      if ([9, 10, 11].includes(monthNumber)) return translate('trendPage.marketTrends.seasons.fall');
      return translate('trendPage.marketTrends.seasons.winter');
    };
  }, [translate]);

  const { processedData, hasSecondaryData, peakInfo } = useMemo(() => {
    if (!marketResponseData?.primary?.market_responses?.lables || !marketResponseData?.primary?.market_responses?.values) {
      return {
        processedData: [],
        hasSecondaryData: false,
        peakInfo: { primaryPeak: null, secondaryPeak: null, peakSeason1: null, peakSeason2: null }
      };
    }

    const primaryData = marketResponseData.primary.market_responses;
    const secondaryData = marketResponseData.secondary?.market_responses;
    const hasSecondaryData = !!(secondaryData?.lables && secondaryData?.values);

    // Create initial data mapping
    const initialData = primaryData.lables.map((koreanMonth, index) => ({
      month: translateMonth(koreanMonth),
      originalMonth: koreanMonth,
      value: primaryData.values[index],
      ...(hasSecondaryData ? {
        value2: secondaryData.values[index]
      } : {}),
      season: getSeasonByKoreanMonth(koreanMonth),
      seasonalConfig
    }));

    // Reorder data to start from March (3월)
    // Order: 3월, 4월, 5월, 6월, 7월, 8월, 9월, 10월, 11월, 12월, 1월, 2월
    const monthOrder = ['3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월', '1월', '2월'];
    const reorderedData = monthOrder.map(koreanMonth =>
      initialData.find(item => item.originalMonth === koreanMonth)
    ).filter(Boolean);

    // Calculate seasonal averages
    const seasonAverages1 = {};
    const seasonAverages2 = {};

    Object.keys(seasonalConfig).forEach(season => {
      const seasonData = reorderedData.filter(item => item.season === season);
      if (seasonData.length > 0) {
        seasonAverages1[season] = seasonData.reduce((sum, item) => sum + item.value, 0) / seasonData.length;

        if (hasSecondaryData) {
          seasonAverages2[season] = seasonData.reduce((sum, item) => sum + (item.value2 || 0), 0) / seasonData.length;
        }
      }
    });

    // Add seasonal averages to each data point
    const processedData = reorderedData.map((item, index) => ({
      ...item,
      seasonalAvg: seasonAverages1[item.season] || 0,
      seasonalAvg2: hasSecondaryData ? (seasonAverages2[item.season] || 0) : null
    }));

    // Find peak month indices in reordered data
    const maxPrimaryValue = Math.max(...processedData.map(d => d.value));
    const primaryPeakIndex = processedData.findIndex(d => d.value === maxPrimaryValue);

    let secondaryPeakIndex = -1;
    if (hasSecondaryData) {
      const maxSecondaryValue = Math.max(...processedData.map(d => d.value2));
      secondaryPeakIndex = processedData.findIndex(d => d.value2 === maxSecondaryValue);
    }

    // Mark peak months
    processedData.forEach((item, index) => {
      item.isPrimaryPeak = index === primaryPeakIndex;
      item.isSecondaryPeak = hasSecondaryData && index === secondaryPeakIndex;
    });

    const peakSeason1 = Object.entries(seasonAverages1).length > 0
      ? Object.entries(seasonAverages1).reduce((a, b) => a[1] > b[1] ? a : b)[0]
      : null;

    let peakSeason2 = null;
    if (hasSecondaryData) {
      peakSeason2 = Object.entries(seasonAverages2).length > 0
        ? Object.entries(seasonAverages2).reduce((a, b) => a[1] > b[1] ? a : b)[0]
        : null;
    }

    return {
      processedData,
      hasSecondaryData,
      peakInfo: {
        primaryPeak: primaryPeakIndex >= 0 ? processedData[primaryPeakIndex] : null,
        secondaryPeak: secondaryPeakIndex >= 0 ? processedData[secondaryPeakIndex] : null,
        peakSeason1,
        peakSeason2
      }
    };
  }, [marketResponseData, translateMonth, getSeasonByKoreanMonth, seasonalConfig]);

  useEffect(() => {
    if (processedData.length > 0) {
      setSelectedSeason(translate('trendPage.marketTrends.seasons.spring'));
    }
  }, [processedData, translate]);

  if (!marketResponseData?.primary?.market_responses?.lables || !marketResponseData?.primary?.market_responses?.values) {
    return (
      <div className="chart-container">
        <div className="chart-error">
          {translate('trendPage.marketTrends.errors.noMarketResponseData')}
        </div>
      </div>
    );
  }

  return (
    <ChartContainer>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header with title and season tabs */}
        <div style={{ marginBottom: '12px' }}>
          {/* Narrative Title */}
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: BBC_STYLE.TEXT_COLOR,
            margin: '0 0 6px 0',
            lineHeight: '1.3'
          }}>
            {createNarrativeTitle(processedData, seasonalConfig, hasSecondaryData, translate)}
          </h2>

          {/* Subtitle */}
          <div style={{
            fontSize: '0.85rem',
            color: BBC_STYLE.TEXT_COLOR,
            margin: '0 0 10px 0',
            lineHeight: '1.4'
          }}>
            {createSubtitle(processedData, hasSecondaryData, translate)}
          </div>

          {/* Season Pills - Horizontal */}
          <div style={{
            display: 'flex',
            gap: '6px',
            padding: '4px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            width: 'fit-content'
          }}>
            {Object.keys(seasonalConfig).map(season => {
              const { icon: Icon, color } = seasonalConfig[season] || {};
              const seasonData = processedData.filter(item => item.season === season);
              const average = seasonData.length > 0
                ? Math.round(seasonData.reduce((sum, item) => sum + item.value, 0) / seasonData.length)
                : 0;
              const isSelected = selectedSeason === season;

              return (
                <div
                  key={season}
                  onClick={() => setSelectedSeason(selectedSeason === season ? null : season)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: isSelected ? '#ffffff' : 'transparent',
                    border: isSelected ? `1.5px solid ${color}` : '1.5px solid transparent',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = `${color}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }
                  }}
                >
                  <Icon size={14} style={{ color, flexShrink: 0 }} />
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: isSelected ? '600' : '500',
                    color: isSelected ? color : BBC_STYLE.TEXT_COLOR
                  }}>
                    {season}
                  </span>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    color: color,
                    fontFamily: BBC_STYLE.FONT_FAMILY,
                    opacity: isSelected ? 1 : 0.6
                  }}>
                    {average}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chart */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={processedData}
              margin={BBC_STYLE.MARGIN}
            >
              <XAxis
                dataKey="month"
                tick={{ fontSize: BBC_STYLE.AXIS_SIZE, fill: BBC_STYLE.AXIS_COLOR }}
                axisLine={{ stroke: BBC_STYLE.AXIS_COLOR }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: BBC_STYLE.AXIS_SIZE, fill: BBC_STYLE.AXIS_COLOR }}
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                label={{
                  value: translate('trendPage.marketTrends.seasonChart.yAxisLabel'),
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fontSize: BBC_STYLE.AXIS_SIZE,
                    fill: BBC_STYLE.AXIS_COLOR
                  }
                }}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} hasSecondaryData={hasSecondaryData} />} />

              {/* Peak month indicator for primary */}
              {peakInfo.primaryPeak && (
                <ReferenceLine
                  x={peakInfo.primaryPeak.month}
                  stroke={BBC_STYLE.PRIMARY_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    value: '★',
                    position: 'top',
                    fill: BBC_STYLE.PRIMARY_COLOR,
                    fontSize: 18,
                    offset: 10
                  }}
                />
              )}

              {/* Peak month indicator for secondary */}
              {hasSecondaryData && peakInfo.secondaryPeak && (
                <ReferenceLine
                  x={peakInfo.secondaryPeak.month}
                  stroke={BBC_STYLE.SECONDARY_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    value: '★',
                    position: 'top',
                    fill: BBC_STYLE.SECONDARY_COLOR,
                    fontSize: 18,
                    offset: peakInfo.secondaryPeak.month === peakInfo.primaryPeak?.month ? 30 : 10
                  }}
                />
              )}

              {/* Step area for seasonal averages - Primary */}
              <Area
                type="step"
                dataKey="seasonalAvg"
                fill={BBC_STYLE.PRIMARY_COLOR}
                fillOpacity={0.15}
                stroke={BBC_STYLE.PRIMARY_COLOR}
                strokeWidth={2}
                strokeOpacity={0.4}
                isAnimationActive={true}
              />

              {/* Step area for seasonal averages - Secondary */}
              {hasSecondaryData && (
                <Area
                  type="step"
                  dataKey="seasonalAvg2"
                  fill={BBC_STYLE.SECONDARY_COLOR}
                  fillOpacity={0.12}
                  stroke={BBC_STYLE.SECONDARY_COLOR}
                  strokeWidth={2}
                  strokeOpacity={0.4}
                  isAnimationActive={true}
                />
              )}

              {/* Triangle bars for individual data points - Primary */}
              <Bar
                dataKey="value"
                name={hasSecondaryData ? translate('trendPage.filters.set1') : ""}
                fill={BBC_STYLE.PRIMARY_COLOR}
                barSize={hasSecondaryData ? 16 : 24}
                shape={(props) => <TriangleBar {...props} selectedSeason={selectedSeason} />}
              />

              {/* Triangle bars for individual data points - Secondary */}
              {hasSecondaryData && (
                <Bar
                  dataKey="value2"
                  name={translate('trendPage.filters.set2')}
                  fill={BBC_STYLE.SECONDARY_COLOR}
                  barSize={16}
                  shape={(props) => <TriangleBar {...props} selectedSeason={selectedSeason} />}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartContainer>
  );
};

SeasonTrendChart.propTypes = {
  marketResponseData: PropTypes.shape({
    primary: PropTypes.shape({
      market_responses: PropTypes.shape({
        lables: PropTypes.arrayOf(PropTypes.string).isRequired,
        values: PropTypes.arrayOf(PropTypes.number).isRequired
      })
    }),
    secondary: PropTypes.shape({
      market_responses: PropTypes.shape({
        lables: PropTypes.arrayOf(PropTypes.string).isRequired,
        values: PropTypes.arrayOf(PropTypes.number).isRequired
      })
    })
  })
};

export default memo(SeasonTrendChart);
